/**
 * Display-level reads over a production task payload, shared by the three
 * ProductionTaskPage layouts (froster driver, baker delivery, default).
 *
 * Pure functions only — no state, no requests. Orchestration stays in the page.
 */
import { isPriorityOrderActive } from "@/lib/priorityOrder";
import { isPriorityTodayOrderActive } from "@/lib/dailyOrder";
import { hubProfileLoginGateMessage } from "@/lib/kitchenHubProfile";
import { isRtgDecorSkip } from "@/lib/productionStageTiming";

export function isPastryTask(info) {
  return Boolean(info?.is_pastry || info?.is_pastry_decor_only || info?.ka_pastry_decor_only);
}

export function taskReferenceImage(info) {
  return info?.pastry_image_url || info?.cake_image_url || "";
}

export function taskWeightOrPiecesLabel(info) {
  if (isPastryTask(info)) {
    const pcs = info?.pastry_pieces || info?.quantity;
    return pcs ? `${pcs} pcs` : "Pastry";
  }
  return info?.weight_kg ? `${info.weight_kg} kg` : null;
}

/**
 * Priority styling for KA task screens. Uses the same `today_order_kind` signal
 * as the dashboard list so the task view and the list agree. Falls back to the
 * legacy is_priority flag only when the field is absent, so nothing regresses.
 */
export function taskPriorityActive(order) {
  if (order && order.today_order_kind != null) {
    return isPriorityTodayOrderActive(order);
  }
  return isPriorityOrderActive(order);
}

/** Commas in orders-app cheesecake filenames must be encoded for reliable loading. */
export function cakeImageSrc(url) {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url, window.location.origin);
    return u.href;
  } catch {
    return encodeURI(url);
  }
}

export function stageStartGateMessage(info, role) {
  const loginGate = hubProfileLoginGateMessage();
  if (loginGate) return loginGate;
  if (!info) return "";
  if (isRtgDecorSkip(info) && (role === "baker" || role === "froster")) {
    return info?.is_pastry || info?.is_pastry_decor_only
      ? "Pastry order — open Decorator tab (box from fridge, no baking or frosting)."
      : "RTG fridge stock — open Decorator tab for this order (no baking or frosting).";
  }
  if (role === "froster" && !info.production_baker_done_at) {
    return "Waiting for baker Done before you can Start.";
  }
  if (role === "decorator") {
    if (info.is_rtg_decor_only || info.rtg_decor_skip_baker_froster) {
      return "";
    }
    if (info.is_dry_cake || info.dry_cake_skip_froster) {
      if (!info.production_baker_done_at) return "Waiting for baker Done before you can Start.";
    } else if (!info.production_froster_done_at) {
      return "Waiting for froster Done before you can Start.";
    }
  }
  return "";
}

export const roleTitle = {
  baker: "Baker",
  froster: "Froster",
  decorator: "Decorator",
};
